import { LoginOutput } from '@allocab/allocab-commons-shared-types';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { clearTokens, updateTokens } from './auth';
import { queryKeys } from '../datastore';
import { storage } from '../storage';

/**
 * Lecture des tokens depuis le stockage. Exportée pour que `getFreshTokens`
 * puisse la donner à `ensureQueryData` et retrouver une session quand le cache
 * react-query est encore vide — au démarrage à froid, typiquement.
 */
export const fetchTokens = () => storage.getJSONItem<LoginOutput>('tokens');

export const useTokens = () => {
  const { data: tokens, isFetched } = useQuery({
    queryKey: queryKeys.tokens(),
    queryFn: fetchTokens,
    staleTime: Infinity,
    gcTime: Infinity,
    networkMode: 'always',
  });

  /**
   * Recopie dans le stockage ce que le cache porte, pour qu'un `updateTokens`
   * survive au redémarrage.
   *
   * La condition est `tokens` et non `tokens !== undefined` : **cet effet ne
   * doit jamais effacer.** L'effacement appartient à `clearTokens`, qui fait
   * déjà `storage.removeItem('tokens')` — la branche `null` ne faisait que le
   * doubler, tout en transformant un cache erroné en perte de données. Un `null`
   * arrivé par erreur dans le cache — lecture d'un stockage pas encore prêt,
   * typiquement — écrasait alors une session parfaitement valide.
   */
  useEffect(() => {
    if (tokens) {
      storage.setJSONItem('tokens', tokens);
    }
  }, [tokens]);

  return { tokens, updateTokens, clearTokens, isFetched };
};
